import { InjectionToken } from '@angular/core';
import { OperatorFunction } from 'rxjs';
import { filter } from 'rxjs/operators';
import { OtherMessage } from './other-handler/interframe-other-handler.model';
import { VlMessage } from './via-lybera/interframe-via-lybera.model';

export const INTERFRAME_GLOBAL_CONFIG = {
    excludeStartMessages: true,
    target: '*',
    bufferSize: 200000,
    version: '1.0'
};

export const CHUNK_MSG_TYPE_NO_ENCODE = '0';
// export const CHUNK_MSG_TYPE_BASE64 = '1';
// export const CHUNK_MSG_TYPE_ZIP_BASE64 = '2';
// export const CHUNK_MESSAGE_FORMAT_JSON = "json";
export const CHUNK_MESSAGE_FORMAT_EMPTY = '';

export type InterFrameCmd = `interframe-`;

export type InterframeMsg = VlMessage | OtherMessage;

/**
 * Base type for interframe messages without payload
 * @template TMsg - The message type identifier (string literal)
 */

type BaseInterframeBilMsg<TMsg extends InterframeMsg = InterframeMsg> = {
    Command: TMsg;
};

/**
 * Type for interframe messages with optional payload
 * @template TMsg - The message type identifier (string literal)
 * @template TPayload - Optional payload type. When provided, adds a `payload` property
 * @example
 * // Message without payload
 * const msg1: InterFrameBilMsg<'action'> = {
 *   Command: 'action'
 * };
 *
 * // Message with payload
 * const msg2: InterFrameBilMsg<'data', { value: number }> = {
 *   Command: 'data',
 *   payload: { value: 42 }
 * };
 */
export type InterFrameBilMsg<
    TMsg extends InterframeMsg = InterframeMsg,
    TPayload = never
> = [TPayload] extends [never]
    ? BaseInterframeBilMsg<TMsg>
    : BaseInterframeBilMsg<TMsg> & { Data: TPayload };

export type InterFrameErrorResponse = InterFrameBilMsg<
    'interframe-vl-error',
    { originalMessage: string; code?: string; message?: string }
>;

/**
 * Type guard to check if a value is an IfBilMessage
 * @param value The value to check
 * @returns True if the value is an IfBilMessage, false otherwise
 */
export const isInterFrameBilMsg = (
    value: unknown
): value is InterFrameBilMsg<InterframeMsg> => {
    return (
        typeof value === 'object' &&
        value !== null &&
        'Command' in value &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        typeof (value as any).Command === 'string' &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (value as any).Command.startsWith('interframe-')
    );
};

/**
 * Custom RxJS operator to filter only InterFrameBilMsg values
 */
export const filterInterFrameBilMsg = <
    TMsg extends InterframeMsg = InterframeMsg
>(): OperatorFunction<unknown, InterFrameBilMsg<TMsg>> =>
    filter(isInterFrameBilMsg) as OperatorFunction<
        unknown,
        InterFrameBilMsg<TMsg>
    >;

export const escapeString = (inputString: string) => {
    return inputString.replace(/[\u007F-\uFFFF]/g, (char) => {
        return '\\u' + ('0000' + char.charCodeAt(0).toString(16)).slice(-4);
    });
};

/**
 * Factory function to create an InterFrameErrorResponse
 * @param payload The error payload containing originalMessage, code, and message
 * @returns A properly formatted InterFrameErrorResponse
 */
export const createInterFrameErrorResponse = (payload: {
    originalMessage: string;
    code?: string;
    message?: string;
}): InterFrameErrorResponse => ({
    Command: 'interframe-vl-error',
    Data: payload
});

export interface InterframeBizHandler {
    handleMessage(msg: InterFrameBilMsg): unknown;
}

export interface InterframeMessageSender {
    sendToParent(msg: InterFrameBilMsg): void;
}

/**
 * Logs a processed message with styled console output
 * @param prefix The service name to display in square brackets
 * @param color The color to use for the background (e.g., 'lightgreen', 'darkblue')
 * @param args The arguments to log to the console
 */
export const logProcessedMsg = (
    prefix: string,
    color: string,
    ...args: unknown[]
): void => {
    const isDarkColor =
        color.includes('dark') ||
        ['blue', 'navy', 'purple', 'maroon', 'black'].includes(
            color.toLowerCase()
        );
    const textColor = isDarkColor ? 'white' : 'black';

    console.log(
        `%c[${prefix}] To be processed msg:`,
        `background-color: ${color}; color: ${textColor}; padding: 2px 4px; border-radius: 2px;`,
        ...args
    );
};

/**
 * Logs an unknown message type with styled console output
 * @param prefix The service name to display in square brackets
 * @param color The base color to use (will be made darker for the background)
 * @param args The arguments to log to the console
 */
export const logUnknownMsg = (
    prefix: string,
    color: string,
    ...args: unknown[]
): void => {
    const darkColor = color.includes('dark') ? color : `dark${color}`;

    console.log(
        `%c[${prefix}] Unknown message type:`,
        `background-color: ${darkColor}; color: white; padding: 2px 4px; border-radius: 2px;`,
        ...args
    );
};

/**
 * Injection token for InterframeBizHandler providers
 */
export const INTERFRAME_BIZ_HANDLER_TOKEN = new InjectionToken<
    readonly InterframeBizHandler[]
>('INTERFRAME_BIZ_HANDLER_TOKEN');

/** Injection token for InterframeMessageSender providers
 */
export const INTERFRAME_MESSAGE_OUTBOUND_TOKEN =
    new InjectionToken<InterframeMessageSender>(
        'INTERFRAME_MESSAGE_OUTBOUND_TOKEN'
    );
